"use client";

import { useMemo, useState } from "react";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Toolbar } from "@/components/ui/toolbar";
import {
  FilterButton,
  type ActiveFilter,
  type FilterFieldDef,
  type SearchableFilterOption,
} from "@/components/ui/filter-panel";
import { useListMembers } from "@/lib/api/generated";
import type { SnippetTab } from "../../lib/types";

export const SNIPPET_TABS: { id: SnippetTab; label: string }[] = [
  { id: "all", label: "All Snippets" },
  { id: "my", label: "My Snippets" },
];

interface SnippetsToolbarProps {
  activeTab: SnippetTab;
  onTabChange: (tab: SnippetTab) => void;
  search: string;
  onSearchChange: (value: string) => void;
  activeFilters: ActiveFilter[];
  onFiltersChange: (filters: ActiveFilter[]) => void;
}

export function SnippetsToolbar({
  activeTab,
  onTabChange,
  search,
  onSearchChange,
  activeFilters,
  onFiltersChange,
}: SnippetsToolbarProps) {
  const [filterOpen, setFilterOpen] = useState(false);
  const { data: membersData } = useListMembers(undefined);

  const filterFields = useMemo<FilterFieldDef[]>(() => {
    const members = membersData?.members ?? [];
    const searchableOptions: SearchableFilterOption[] = members.map((m) => ({
      id: m.id,
      label: `${m.first_name} ${m.last_name}`.trim() || m.email,
      avatarUrl: m.avatar_url ?? null,
    }));
    return [
      {
        key: "creator",
        label: "Creator",
        icon: "IconUser",
        type: "searchable",
        options: [],
        searchableOptions,
        placeholder: "Select creator…",
      },
    ];
  }, [membersData?.members]);

  const handleFiltersChange = (filters: ActiveFilter[]) => {
    onFiltersChange(filters);
  };

  return (
    <Toolbar.Container>
      <Tabs
        value={activeTab}
        onValueChange={(v) => onTabChange(v as SnippetTab)}
        size="sm"
      >
        <TabsList>
          {SNIPPET_TABS.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Toolbar.Separator />

      <Toolbar.Search
        value={search}
        onChange={onSearchChange}
        className="w-full sm:w-40"
      />

      <Toolbar.Separator />

      <FilterButton
        fields={filterFields}
        filters={activeFilters}
        onFiltersChange={handleFiltersChange}
        open={filterOpen}
        onOpenChange={setFilterOpen}
      />
    </Toolbar.Container>
  );
}
